import React, { useContext } from 'react';
import { Form, Input, Select, message } from 'antd';
import { countryCodes } from '@leedpulse-monorepo/utils';
import { ContextStatus, EPhonePlatform, phonePlatformAsString, EChannel, channelAsString, numberProductsAsString, ENumberProducts } from '@leedpulse-monorepo/enums';
import {
  FormCard,
  FormContainer,
  FormSectionTitle,
} from '@leedpulse-monorepo/components';
import { NUMBERS_PAGE_PATH } from '../../pages/numbers/NumbersPage';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'antd/es/form/Form';
import { INumber } from '@leedpulse-monorepo/interfaces';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import createNumber from '../../../domain/use_cases/numbers/createNumber';

const { Option } = Select;

const NumberCreationForm: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthenticationContext);

  const [form] = useForm<INumber>();
  const [contextStatus, setContextStatus] = React.useState<ContextStatus>(ContextStatus.IDLE);

  const handleSubmit = async (values: INumber) => {
    if (!authContext.authentication) {
      message.error('Vous devez être connecté pour effectuer cette action');
      return;
    }

    setContextStatus(ContextStatus.LOADING);
    try {
      const createdAt = new Date().getTime();
      await createNumber(
        {
          available: true,
          channel: values.channel || EChannel.sms, // Set a default value for channel if it is null
          connector_whatsapp: values.connector_whatsapp || {},
          country: values.country || 'france', // Set a default value for country if it is null
          created: createdAt,
          group_id: values.group_id,
          group_name: values.group_name,
          notes: values.notes,
          product: values.product || ENumberProducts.all,
          provider: values.provider || EPhonePlatform.callr,
          redirection: values.redirection || '',
          sender: values.sender,
          updated: createdAt,
          ai_receptionist: { enabled: false, public_label: '', callback_label: '' },
        },
        authContext.authentication
      );
      setContextStatus(ContextStatus.SUCCESS);
      message.success('Numéro créé avec succès');
      navigate(`${NUMBERS_PAGE_PATH}/${values.group_id}`);
    } catch (e) {
      setContextStatus(ContextStatus.ERROR);
    }
  };

  return (
    <FormCard
      title="Nouveau Numéro"
      showTitle={false}
      onCreated={() => form.submit()}
      isCreateLoading={contextStatus === ContextStatus.LOADING}
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <FormSectionTitle title="Informations générales" />
        <FormContainer>
          <Form.Item
            label="Numéro"
            name="sender"
            rules={[{ required: true, message: 'Veuillez entrer un numéro' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="ID"
            name="group_id"
            rules={[{ required: true, message: 'Veuillez entrer un ID' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Nom"
            name="group_name"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Fournisseur"
            name="provider"
            rules={[{ required: true, message: 'Veuillez sélectionner un fournisseur' }]}
          >
            <Select>
              {Object.keys(EPhonePlatform).map((key) => (
                <Option
                  key={key}
                  value={EPhonePlatform[key as keyof typeof EPhonePlatform]}
                >
                  {phonePlatformAsString(
                    EPhonePlatform[key as keyof typeof EPhonePlatform]
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Canal" name="channel">
            <Select>
              {form.getFieldValue('provider') === EPhonePlatform.twilio ? (
                <Option key={EChannel.whatsapp} value={EChannel.whatsapp}>
                  {channelAsString(EChannel.whatsapp)}
                </Option>
              ) : (
                <>
                  <Option key={EChannel.sms} value={EChannel.sms}>
                    {channelAsString(EChannel.sms)}
                  </Option>
                  <Option key={EChannel.redirecting} value={EChannel.redirecting}>
                    {channelAsString(EChannel.redirecting)}
                  </Option>
                  <Option key={EChannel.showcase} value={EChannel.showcase}>
                    {channelAsString(EChannel.showcase)}
                  </Option>
                </>
              )}
            </Select>
          </Form.Item>
          <Form.Item label="Pays" name="country">
            <Select>
              {countryCodes.map((country) => (
                <Option key={country.name} value={country.name}>
                  {country.flag} {country.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Produit" name="product">
            <Select>
              {Object.keys(ENumberProducts).map((key) => (
                <Option
                  key={key}
                  value={ENumberProducts[key as keyof typeof ENumberProducts]}
                >
                  {numberProductsAsString(
                    ENumberProducts[key as keyof typeof ENumberProducts]
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <TextArea />
          </Form.Item>
        </FormContainer>
      </Form>
    </FormCard>
  );
};

export default NumberCreationForm;
